import React, { useState, ReactNode } from 'react'
import {
  AddDomainContainer,
  AddDomainModalDescription,
  AddDomainModalInput,
  AddDomainModalStyled,
  DomainModalError,
  InputError,
} from 'Components/Elements/AddDomainModal/SingleDomain/AddDomainModal.styled'

import { Trans, useTranslation } from 'react-i18next'
import { useAddDomain } from 'Features/domainsManagement/api/addDomain'
import { useGetErrorText } from 'Utils/errorText'
import { validateDomain } from 'Utils/validation'
import {
  Button,
  ButtonWithIcon,
  InputWithIcon,
  ModalControlsStyled,
  StyledText,
  Toast,
  useFrontEndEventTrackingService,
  useTriggerOnPressEnter,
} from '@clickcease/cc-client-components-lib'
import { Plus } from 'Assets/icons/Plus'
import { CSS } from '@stitches/react'
import { useAllowedLimits } from 'src/hooks/useAllowedLimits'
import { MessageBoxContainer } from '../MultipleDomains/MultipleDomains.styles'
import { trimPrototcolAndWww } from 'Utils/routes'

interface AddDomainModalProps {
  onOpenChange: (domainId: number) => void
  description?: ReactNode
  css?: CSS
  origin?: string
}

export const AddDomainInner = ({ onOpenChange, description, css, origin }: AddDomainModalProps) => {
  const { t } = useTranslation('domainManagement')
  const { track } = useFrontEndEventTrackingService()
  const { mutate, isLoading, isError, error } = useAddDomain()
  const { getErrorText } = useGetErrorText()
  const { allowedProducts, domainsLeft } = useAllowedLimits()
  const [inputValue, setInputValue] = useState('')
  const [inputError, setInputError] = useState<string>('')
  const showInputError = !!inputError.length

  const noDomainsLeftInAccount =
    allowedProducts.botZapping &&
    domainsLeft.botZapping <= 0 &&
    allowedProducts.paidMarketing &&
    domainsLeft.paidMarketing <= 0

  const productLimitReached =
    (allowedProducts.botZapping && domainsLeft.botZapping <= 0) ||
    (allowedProducts.paidMarketing && domainsLeft.paidMarketing <= 0)

  const isButtonDisabled =
    !(inputValue.length && validateDomain(inputValue)) || noDomainsLeftInAccount

  const handleDomainInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
  }

  const onSuccess = (domainId: number) => {
    if (origin === 'onboarding') {
      track(['segment'], `Domain added with no credit card flow`, {})
    }
    onOpenChange(domainId)
  }

  const validateInput = () => {
    if (!inputValue.length) {
      setInputError(t('add_new_domain_modal.empty_domain'))
    } else if (!validateDomain(inputValue)) {
      setInputError(t('add_new_domain_modal.invalid_domain'))
    } else {
      setInputError('')
    }
  }

  const addNewDomain = () => {
    if (validateDomain(inputValue)) {
      const cleanAddress = trimPrototcolAndWww(inputValue?.toLowerCase())?.toLowerCase()
      const validatedDomains = [
        {
          address: cleanAddress,
          ...allowedProducts,
        },
      ]

      mutate(
        { domains: validatedDomains },
        {
          onSuccess: (res) => {
            if (!(res instanceof Error)) {
              onSuccess(res.data.addedDomains[0].id)
              track(['dataLayer'], 'Onboarding.addedFirstDomain', {
                domain: res.data.addedDomains[0].address,
              })
            }
          },
        }
      )
    }
  }

  useTriggerOnPressEnter(addNewDomain)

  return (
    <AddDomainModalStyled>
      <AddDomainContainer>
        <AddDomainModalDescription css={css}>
          {description}
          <AddDomainModalInput>
            <InputWithIcon
              value={inputValue}
              onBlur={validateInput}
              placeholder={t('add_new_domain_modal.example')}
              onChange={handleDomainInput}
              error={showInputError}
            />
            {showInputError && <InputError>{inputError}</InputError>}
          </AddDomainModalInput>
        </AddDomainModalDescription>
        {isError && <DomainModalError>{getErrorText(error)}</DomainModalError>}
      </AddDomainContainer>

      {productLimitReached && (
        <MessageBoxContainer singleDomainVariant>
          <Toast type={'warning'}>
            <StyledText variant={'baseLight'}>
              <Trans
                ns={'domainManagement'}
                i18nKey={'add_new_domain_modal.exceeded_product_limit'}
                values={{
                  product: domainsLeft.paidMarketing <= 0 ? t('paid_marketing') : t('bot_zapping'),
                }}
              />
            </StyledText>
          </Toast>
        </MessageBoxContainer>
      )}

      <ModalControlsStyled css={{ borderTop: 'none' }}>
        <Button
          variant="ghost"
          text={t('add_new_domain_modal.cancel')}
          onClick={() => {
            onOpenChange(0)
          }}
        />
        <ButtonWithIcon
          iconLeft={
            <Plus
              stroke={'var(--colors-white)'}
              fill={isButtonDisabled ? 'var(--colors-lightNeutral)' : 'var(--colors-primaryColor)'}
            />
          }
          disabled={isButtonDisabled}
          text={t('add_new_domain_modal.add_domains')}
          loading={isLoading}
          onClick={addNewDomain}
        />
      </ModalControlsStyled>
    </AddDomainModalStyled>
  )
}
